import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi } from '../lib/api';
import api from '../lib/api';
import { getTodayString, formatCurrency, formatNumber } from '../lib/utils';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { RestaurantSelector } from '../components/dashboard/RestaurantSelector';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    Users, 
    TrendingUp,
    TrendingDown,
    Utensils,
    Moon,
    CalendarDays,
    Calendar,
    BarChart3,
    Info,
    ChevronDown
} from 'lucide-react';

// Jours de la semaine
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const JOURS_COMPLETS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MOIS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// Génère la liste des mois disponibles (de janvier de l'année en cours jusqu'au mois actuel)
const getAvailableMonths = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    
    const months = [];
    
    // Option YTD Annuel en premier
    months.push({
        value: 'ytd_year',
        label: `YTD ${currentYear}`,
        mode: 'ytd_year'
    });
    
    // Mois du plus récent au plus ancien
    for (let m = currentMonth; m >= 0; m--) {
        const monthValue = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
        const isCurrentMonth = m === currentMonth;
        months.push({
            value: monthValue,
            label: isCurrentMonth ? `${MOIS_FR[m]} ${currentYear} (en cours)` : `${MOIS_FR[m]} ${currentYear}`,
            mode: isCurrentMonth ? 'ytd_month' : 'month'
        });
    }
    
    return months;
};

// Composant Histogramme avec barres verticales (jours en abscisses)
const HistogrammeComparatif = ({ data, title, icon: Icon, type = "couverts", restaurantName = "" }) => {
    if (!data || data.length === 0) return null;
    
    const isMaBiche = restaurantName === "Ma biche sur le toit";
    
    // Trouver le max pour l'échelle
    const maxVal = Math.max(
        ...data.map(d => Math.max(d.budget || 0, d.realise || 0)),
        1
    );
    
    // Trouver l'écart max pour la partie écart
    const maxEcartPositif = Math.max(...data.map(d => Math.max(0, (d.realise || 0) - (d.budget || 0))), 0);
    const maxEcartNegatif = Math.abs(Math.min(...data.map(d => Math.min(0, (d.realise || 0) - (d.budget || 0))), 0));
    const maxEcart = Math.max(maxEcartPositif, maxEcartNegatif, 1);
    
    const formatValue = (val) => {
        if (type === "currency") return formatCurrency(val);
        return Math.round(val);
    };
    
    return (
        <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
                {Icon && <Icon className="w-5 h-5 text-primary" />}
                <h3 className="font-semibold text-base">{title}</h3>
            </div>
            
            {/* Légende */}
            <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span className="text-muted-foreground">Réalisé</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-gray-300"></div>
                    <span className="text-muted-foreground">Budget</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span className="text-muted-foreground">Écart +</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-red-500"></div>
                    <span className="text-muted-foreground">Écart -</span>
                </div>
            </div>
            
            {/* Histogramme vertical */}
            <div className="flex items-end justify-between gap-1 pt-8 px-1">
                {data.map((item, idx) => {
                    const budgetPct = (item.budget / maxVal) * 100;
                    const realisePct = (item.realise / maxVal) * 100;
                    const ecart = item.realise - item.budget;
                    const ecartPct = item.budget > 0 ? ((ecart / item.budget) * 100).toFixed(0) : 0;
                    const ecartBarPct = (Math.abs(ecart) / maxEcart) * 50; // 50% max height for ecart bar
                    
                    // Données BtoB pour le tooltip
                    const hasBtobData = item.btob_pax > 0 || item.btob_tm > 0;
                    
                    return (
                        <div key={idx} className="flex-1 flex flex-col items-center group relative">
                            {/* Tooltip au survol */}
                            <div className="absolute -top-24 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-20 whitespace-nowrap pointer-events-none shadow-lg">
                                <div className="font-semibold mb-1">{JOURS_COMPLETS[idx]}</div>
                                <div>Réalisé: {formatValue(item.realise)}</div>
                                <div>Budget: {formatValue(item.budget)}</div>
                                <div className={ecart >= 0 ? 'text-green-400' : 'text-red-400'}>
                                    Écart: {ecart >= 0 ? '+' : ''}{formatValue(ecart)}
                                </div>
                                {/* Afficher BtoB pour Ma biche (couverts) ou pour tous (TM) */}
                                {type === "couverts" && isMaBiche && item.btob_pax > 0 && (
                                    <div className="text-yellow-400 mt-1 border-t border-gray-700 pt-1">
                                        PAX BtoB: {Math.round(item.btob_pax)}
                                    </div>
                                )}
                                {type === "currency" && isMaBiche && item.btob_tm > 0 && (
                                    <div className="text-yellow-400 mt-1 border-t border-gray-700 pt-1">
                                        TM BtoB: {formatCurrency(item.btob_tm)}
                                    </div>
                                )}
                            </div>
                            
                            {/* Écart en % en haut */}
                            <div className={`text-[10px] font-bold mb-2 ${
                                ecart >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {ecart !== 0 ? (ecart >= 0 ? '+' : '') + ecartPct + '%' : ''}
                            </div>
                            
                            {/* Container global avec axe au milieu */}
                            <div className="w-full flex flex-col items-center h-40">
                                {/* Partie HAUTE : Barres Réalisé, Budget + Écarts positifs */}
                                <div className="w-full flex justify-center gap-0.5 h-20 items-end">
                                    {/* Barre Réalisé */}
                                    <div 
                                        className="w-3 bg-blue-500 rounded-t transition-all duration-300"
                                        style={{ height: `${realisePct}%`, minHeight: item.realise > 0 ? '2px' : '0' }}
                                    />
                                    {/* Barre Budget */}
                                    <div 
                                        className="w-3 bg-gray-300 rounded-t transition-all duration-300"
                                        style={{ height: `${budgetPct}%`, minHeight: item.budget > 0 ? '2px' : '0' }}
                                    />
                                    {/* Écart positif (vert) - monte vers le haut */}
                                    <div 
                                        className={`w-3 rounded-t transition-all duration-300 ${ecart > 0 ? 'bg-green-500' : 'bg-transparent'}`}
                                        style={{ height: ecart > 0 ? `${ecartBarPct}%` : '0', minHeight: ecart > 0 ? '2px' : '0' }}
                                    />
                                </div>
                                
                                {/* LIGNE DE L'AXE HORIZONTAL */}
                                <div className="w-full h-px bg-gray-300"></div>
                                
                                {/* Partie BASSE : Écarts négatifs uniquement */}
                                <div className="w-full flex justify-center gap-0.5 h-20 items-start">
                                    {/* Espaces vides pour Réalisé et Budget */}
                                    <div className="w-3"></div>
                                    <div className="w-3"></div>
                                    {/* Écart négatif (rouge) - descend vers le bas */}
                                    <div 
                                        className={`w-3 rounded-b transition-all duration-300 ${ecart < 0 ? 'bg-red-500' : 'bg-transparent'}`}
                                        style={{ height: ecart < 0 ? `${ecartBarPct}%` : '0', minHeight: ecart < 0 ? '2px' : '0' }}
                                    />
                                </div>
                            </div>
                            
                            {/* Jour en abscisse */}
                            <span className="mt-2 text-xs font-medium text-muted-foreground">
                                {JOURS[idx]}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Composant Tableau récapitulatif des écarts
const TableauEcarts = ({ data, title }) => {
    const [hoveredCell, setHoveredCell] = useState(null);
    
    if (!data) return null;
    
    const renderEcart = (ecart, type, period) => {
        const isPositif = ecart >= 0;
        const detailData = data?.[period]?.[type]?.par_jour;
        
        return (
            <td 
                className="relative px-4 py-3 text-center cursor-help"
                onMouseEnter={() => setHoveredCell(`${type}-${period}`)}
                onMouseLeave={() => setHoveredCell(null)}
            >
                <span className={`font-semibold ${isPositif ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositif ? '+' : ''}{Math.round(ecart)}
                </span>
                
                {/* Tooltip détail par jour */}
                {hoveredCell === `${type}-${period}` && detailData && (
                    <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-[200px]">
                        <div className="font-semibold mb-2 border-b border-gray-700 pb-1">
                            Détail par jour
                        </div>
                        <div className="space-y-1">
                            {JOURS_COMPLETS.map((jour) => {
                                const jourData = detailData[jour];
                                if (!jourData) return null;
                                const jourEcart = jourData.ecart || 0;
                                return (
                                    <div key={jour} className="flex justify-between">
                                        <span>{jour}</span>
                                        <span className={jourEcart >= 0 ? 'text-green-400' : 'text-red-400'}>
                                            {jourEcart >= 0 ? '+' : ''}{Math.round(jourEcart)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                            <div className="border-8 border-transparent border-t-gray-900"></div>
                        </div>
                    </div>
                )}
            </td>
        );
    };
    
    return (
        <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">{title}</h3>
                <Info className="w-4 h-4 text-muted-foreground ml-2" />
                <span className="text-xs text-muted-foreground">Survolez pour le détail</span>
            </div>
            
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-4 py-2 text-left font-medium text-muted-foreground"></th>
                            <th className="px-4 py-2 text-center font-medium text-muted-foreground" colSpan={2}>
                                <div className="flex items-center justify-center gap-2">
                                    <Utensils className="w-4 h-4" />
                                    Midi
                                </div>
                            </th>
                            <th className="px-4 py-2 text-center font-medium text-muted-foreground" colSpan={2}>
                                <div className="flex items-center justify-center gap-2">
                                    <Moon className="w-4 h-4" />
                                    Soir
                                </div>
                            </th>
                        </tr>
                        <tr className="border-b border-border text-xs">
                            <th className="px-4 py-2 text-left"></th>
                            <th className="px-4 py-2 text-center text-muted-foreground">Ce mois</th>
                            <th className="px-4 py-2 text-center text-muted-foreground">Cette année</th>
                            <th className="px-4 py-2 text-center text-muted-foreground">Ce mois</th>
                            <th className="px-4 py-2 text-center text-muted-foreground">Cette année</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-border hover:bg-muted/30">
                            <td className="px-4 py-3 font-medium">Couverts</td>
                            {renderEcart(data?.month?.couverts_midi?.ecart || 0, 'couverts_midi', 'month')}
                            {renderEcart(data?.year?.couverts_midi?.ecart || 0, 'couverts_midi', 'year')}
                            {renderEcart(data?.month?.couverts_soir?.ecart || 0, 'couverts_soir', 'month')}
                            {renderEcart(data?.year?.couverts_soir?.ecart || 0, 'couverts_soir', 'year')}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Composant principal
const VentesDetailleesPage = () => {
    const { user } = useAuth();
    const [selectedDate, setSelectedDate] = useState(getTodayString());
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [mode, setMode] = useState('ytd_month'); // 'ytd_month', 'month', 'ytd_year', ou 'week'
    const [selectedMonth, setSelectedMonth] = useState(null); // Format "YYYY-MM"
    const [shift, setShift] = useState('midi'); // 'midi' ou 'soir'
    const [couvertsData, setCouvertsData] = useState(null);
    const [tmData, setTmData] = useState(null);
    const [ecartsData, setEcartsData] = useState(null);
    const [extrasData, setExtrasData] = useState(null); // PDJ, Limo, Night
    const [extrasAggregation, setExtrasAggregation] = useState('moyenne'); // 'moyenne' ou 'somme'
    const [loading, setLoading] = useState(true);
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);
    
    // Liste des mois disponibles
    const availableMonths = useMemo(() => getAvailableMonths(), []);
    
    // Label du mois sélectionné
    const selectedMonthLabel = useMemo(() => {
        if (mode === 'week') return null;
        if (mode === 'ytd_year') return `YTD ${new Date().getFullYear()}`;
        if (mode === 'ytd_month') {
            const now = new Date();
            return `${MOIS_FR[now.getMonth()]} ${now.getFullYear()} (en cours)`;
        }
        if (selectedMonth) {
            const [year, month] = selectedMonth.split('-');
            return `${MOIS_FR[parseInt(month) - 1]} ${year}`;
        }
        return 'Sélectionner un mois';
    }, [mode, selectedMonth]);

    // Fetch restaurants
    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                setRestaurants(response.data);
                
                if (response.data.length > 0) {
                    // Ma biche sur le toit par défaut
                    const maBiche = response.data.find(r => r.name === "Ma biche sur le toit");
                    if (maBiche) {
                        setSelectedRestaurant(maBiche);
                    } else {
                        setSelectedRestaurant(response.data[0]);
                    }
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error);
                toast.error('Erreur lors du chargement des restaurants');
            }
        };
        fetchRestaurants();
    }, [user?.restaurant_id]);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (!selectedRestaurant) return;
        
        setLoading(true);
        
        try {
            // Construire les paramètres de requête
            let queryParams = `mode=${mode}&date_str=${selectedDate}`;
            if (mode === 'month' && selectedMonth) {
                queryParams += `&selected_month=${selectedMonth}`;
            }
            
            const [couvertsRes, tmRes, ecartsRes] = await Promise.allSettled([
                api.get(`/ventes/couverts-semaine/${encodeURIComponent(selectedRestaurant.name)}?${queryParams}`),
                api.get(`/ventes/ticket-moyen-semaine/${encodeURIComponent(selectedRestaurant.name)}?${queryParams}`),
                api.get(`/ventes/ecarts-ytd/${encodeURIComponent(selectedRestaurant.name)}?date_str=${selectedDate}`)
            ]);
            
            if (couvertsRes.status === 'fulfilled') {
                setCouvertsData(couvertsRes.value.data);
            }
            
            if (tmRes.status === 'fulfilled') {
                setTmData(tmRes.value.data);
            }
            
            if (ecartsRes.status === 'fulfilled') {
                setEcartsData(ecartsRes.value.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [selectedRestaurant, mode, selectedDate, selectedMonth]);

    // Fetch extras data séparément (utilise le même sélecteur de mois que le reste)
    const fetchExtrasData = useCallback(async () => {
        if (!selectedRestaurant) return;
        
        try {
            // Utiliser le même mode et mois sélectionné que les autres sections
            let extrasParams = `mode=${mode}&date_str=${selectedDate}&aggregation=${extrasAggregation}`;
            if (mode === 'month' && selectedMonth) {
                extrasParams += `&selected_month=${selectedMonth}`;
            }
            
            const extrasRes = await api.get(`/ventes/ca-extras-semaine/${encodeURIComponent(selectedRestaurant.name)}?${extrasParams}`);
            setExtrasData(extrasRes.data);
        } catch (error) {
            console.error('Error fetching extras data:', error);
        }
    }, [selectedRestaurant, selectedDate, mode, selectedMonth, extrasAggregation]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchExtrasData();
    }, [fetchExtrasData]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleRestaurantSelect = (restaurant) => {
        // Vérifier si c'est Jacques Blagnac (caisse non connectée)
        if (restaurant.name === "Jacques Blagnac") {
            toast.error("Caisse non connectée", {
                description: "Nous n'arrivons pas encore à nous connecter à la caisse de Jacques Blagnac. Les données de ventes ne sont pas disponibles.",
                duration: 5000
            });
            return; // Ne pas sélectionner ce restaurant
        }
        setSelectedRestaurant(restaurant);
    };
    
    const handleMonthSelect = (monthOption) => {
        setMode(monthOption.mode);
        if (monthOption.mode === 'month') {
            setSelectedMonth(monthOption.value);
        } else {
            setSelectedMonth(null);
        }
        setShowMonthDropdown(false);
    };
    
    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showMonthDropdown && !e.target.closest('[data-testid="month-selector-btn"]') && !e.target.closest('[data-testid^="month-option-"]')) {
                setShowMonthDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showMonthDropdown]);

    return (
        <div className="flex h-screen bg-background" data-testid="ventes-detaillees-page">
            <Sidebar />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header 
                    title={selectedRestaurant?.name || 'Ventes détaillées'}
                    subtitle="Couverts & Ticket Moyen par jour"
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                />

                <main className="flex-1 overflow-auto p-6">
                    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
                        
                        {/* Sélecteur de restaurant avec logos */}
                        <div className="bg-card border border-border rounded-xl p-4">
                            <RestaurantSelector
                                restaurants={restaurants}
                                selectedId={selectedRestaurant?.id}
                                onSelect={handleRestaurantSelect}
                            />
                        </div>
                        
                        {/* Toggle Mode Période avec liste déroulante des mois */}
                        <div className="flex items-center justify-between gap-4 bg-card border border-border rounded-xl p-4">
                            {/* Toggle Midi / Soir */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-muted-foreground">Service :</span>
                                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                                    <button
                                        onClick={() => setShift('midi')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                                            shift === 'midi' 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                        data-testid="shift-midi-btn"
                                    >
                                        <Utensils className="w-4 h-4" />
                                        Midi
                                    </button>
                                    <button
                                        onClick={() => setShift('soir')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                                            shift === 'soir' 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                        data-testid="shift-soir-btn"
                                    >
                                        <Moon className="w-4 h-4" />
                                        Soir
                                    </button>
                                </div>
                            </div>
                            
                            {/* Sélecteur de période */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-muted-foreground">Période :</span>
                                <div className="flex items-center gap-2">
                                    {/* Liste déroulante des mois */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 min-w-[200px] justify-between ${
                                                mode !== 'week' 
                                                    ? 'bg-primary text-primary-foreground' 
                                                    : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                            }`}
                                            data-testid="month-selector-btn"
                                        >
                                            <div className="flex items-center gap-2">
                                                <CalendarDays className="w-4 h-4" />
                                                <span>{selectedMonthLabel}</span>
                                            </div>
                                            <ChevronDown className={`w-4 h-4 transition-transform ${showMonthDropdown ? 'rotate-180' : ''}`} />
                                        </button>
                                        
                                        {/* Dropdown */}
                                        {showMonthDropdown && (
                                            <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                                                {availableMonths.map((monthOption) => (
                                                    <button
                                                        key={monthOption.value}
                                                        onClick={() => handleMonthSelect(monthOption)}
                                                        className={`w-full px-4 py-2 text-sm text-left hover:bg-muted/50 transition-colors ${
                                                            (mode === monthOption.mode && (monthOption.mode !== 'month' || selectedMonth === monthOption.value))
                                                                ? 'bg-primary/10 text-primary font-medium'
                                                                : 'text-foreground'
                                                        }`}
                                                        data-testid={`month-option-${monthOption.value}`}
                                                    >
                                                        {monthOption.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Bouton Semaine */}
                                    <button
                                        onClick={() => {
                                            setMode('week');
                                            setShowMonthDropdown(false);
                                        }}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                                            mode === 'week' 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                        }`}
                                        data-testid="mode-week-btn"
                                    >
                                        <Calendar className="w-4 h-4" />
                                        Semaine
                                    </button>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="space-y-6">
                                <Skeleton className="h-64 w-full rounded-xl" />
                                <Skeleton className="h-64 w-full rounded-xl" />
                                <Skeleton className="h-32 w-full rounded-xl" />
                            </div>
                        ) : (
                            <>
                                {/* Section Couverts et TM - Masqué pour Coup de bambou le soir */}
                                {!(selectedRestaurant?.name?.toLowerCase().includes('coup de bambou') && shift === 'soir') && (
                                    <>
                                        {/* Warning si tickets suspects */}
                                        {(couvertsData?.warnings?.jours_suspects?.length > 0 || tmData?.warnings?.jours_suspects?.length > 0) && (
                                            <div className="relative group">
                                                <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-600 dark:text-amber-400">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                                    </svg>
                                                    <span className="text-sm font-medium">
                                                        Données potentiellement erronées sur certains jours
                                                    </span>
                                                </div>
                                                {/* Tooltip au survol */}
                                                <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
                                                    <div className="bg-card border border-border rounded-lg shadow-lg p-3 max-w-xs">
                                                        <p className="text-sm text-muted-foreground">
                                                            ⚠️ Attention : un ou plusieurs tickets paraissent erronés en nombre de couverts (&gt;20 couverts sur un seul ticket)
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Section Couverts */}
                                        <div className="space-y-4">
                                            <h2 className="text-lg font-bold flex items-center gap-2">
                                                <Users className="w-5 h-5 text-primary" />
                                                Couverts par jour de la semaine
                                                <span className="text-sm font-normal text-muted-foreground ml-2">
                                                    ({couvertsData?.period_label || (mode === 'week' ? 'Semaine en cours' : 'Moyenne mensuelle')})
                                                </span>
                                            </h2>
                                            
                                            {/* Un seul histogramme Couverts selon le shift */}
                                            <HistogrammeComparatif 
                                                data={shift === 'midi' ? couvertsData?.midi_data : couvertsData?.soir_data}
                                                title={shift === 'midi' ? "Couverts Midi (11h30 - 16h)" : "Couverts Soir (18h45 - 00h)"}
                                                icon={shift === 'midi' ? Utensils : Moon}
                                                type="couverts"
                                                restaurantName={selectedRestaurant?.name}
                                            />
                                            
                                            {/* Tableau récapitulatif couverts */}
                                            <TableauEcarts 
                                                data={ecartsData}
                                                title="Écart couverts vs Budget"
                                            />
                                        </div>
                                        
                                        {/* Séparateur */}
                                        <div className="border-t border-border my-8"></div>
                                        
                                        {/* Section Ticket Moyen */}
                                        <div className="space-y-4">
                                            <h2 className="text-lg font-bold flex items-center gap-2">
                                                <TrendingUp className="w-5 h-5 text-primary" />
                                                Ticket Moyen par jour de la semaine
                                                <span className="text-sm font-normal text-muted-foreground ml-2">
                                                    ({tmData?.period_label || (mode === 'week' ? 'Semaine en cours' : 'Moyenne mensuelle')})
                                                </span>
                                            </h2>
                                            
                                            {/* Un seul histogramme TM selon le shift */}
                                            <HistogrammeComparatif 
                                                data={shift === 'midi' ? tmData?.midi_data : tmData?.soir_data}
                                                title={shift === 'midi' ? "Ticket Moyen Midi" : "Ticket Moyen Soir"}
                                                icon={shift === 'midi' ? Utensils : Moon}
                                                type="currency"
                                                restaurantName={selectedRestaurant?.name}
                                            />
                                            
                                            {/* Résumé TM */}
                                            <div className="bg-card border border-border rounded-xl p-5">
                                                <h3 className="font-semibold text-base mb-4">Résumé Ticket Moyen {shift === 'midi' ? 'Midi' : 'Soir'}</h3>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                                                        <div className="text-xs text-muted-foreground mb-1">TM {shift === 'midi' ? 'Midi' : 'Soir'} Réalisé</div>
                                                        <div className="text-xl font-bold">
                                                            {formatCurrency(shift === 'midi' ? (tmData?.totals?.midi?.realise || 0) : (tmData?.totals?.soir?.realise || 0))}
                                                        </div>
                                                    </div>
                                                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                                                        <div className="text-xs text-muted-foreground mb-1">TM {shift === 'midi' ? 'Midi' : 'Soir'} Budget</div>
                                                        <div className="text-xl font-bold text-muted-foreground">
                                                            {formatCurrency(shift === 'midi' ? (tmData?.totals?.midi?.budget || 0) : (tmData?.totals?.soir?.budget || 0))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Séparateur */}
                                        <div className="border-t border-border my-8"></div>
                                    </>
                                )}
                                
                                {/* Section CA Extras (PDJ, Limonade, Night) */}
                                <div className="space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <h2 className="text-lg font-bold flex items-center gap-2">
                                            <BarChart3 className="w-5 h-5 text-primary" />
                                            CA Extras par jour de la semaine
                                            <span className="text-sm font-normal text-muted-foreground ml-2">
                                                ({extrasData?.period_label || 'Chargement...'})
                                            </span>
                                        </h2>
                                        
                                        {/* Toggle Moyenne/Total uniquement */}
                                        <div className="flex items-center bg-muted/50 rounded-lg p-1">
                                            <button
                                                onClick={() => setExtrasAggregation('moyenne')}
                                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                    extrasAggregation === 'moyenne'
                                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground'
                                                }`}
                                            >
                                                Moyenne
                                            </button>
                                            <button
                                                onClick={() => setExtrasAggregation('somme')}
                                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                    extrasAggregation === 'somme'
                                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground'
                                                }`}
                                            >
                                                Total
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="text-xs text-muted-foreground mb-4 flex flex-wrap gap-4">
                                        <span>• PDJ: 6h00-12h00 (sans couverts)</span>
                                        <span>• Limonade: 14h30-19h00 (sans couverts)</span>
                                        <span>• Night: 19h00-6h00 (sans couverts)</span>
                                    </div>
                                    
                                    {/* Tableau CA Extras */}
                                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted/30">
                                                <tr className="border-b border-border">
                                                    <th className="px-4 py-3 text-left font-semibold">Jour</th>
                                                    <th className="px-4 py-3 text-center font-semibold" colSpan={3}>Petit Déjeuner</th>
                                                    <th className="px-4 py-3 text-center font-semibold" colSpan={3}>Limonade</th>
                                                    <th className="px-4 py-3 text-center font-semibold" colSpan={3}>Night</th>
                                                </tr>
                                                <tr className="border-b border-border text-xs text-muted-foreground">
                                                    <th className="px-4 py-2 text-left"></th>
                                                    <th className="px-2 py-2 text-center">Budget</th>
                                                    <th className="px-2 py-2 text-center">Réalisé</th>
                                                    <th className="px-2 py-2 text-center">Écart</th>
                                                    <th className="px-2 py-2 text-center">Budget</th>
                                                    <th className="px-2 py-2 text-center">Réalisé</th>
                                                    <th className="px-2 py-2 text-center">Écart</th>
                                                    <th className="px-2 py-2 text-center">Budget</th>
                                                    <th className="px-2 py-2 text-center">Réalisé</th>
                                                    <th className="px-2 py-2 text-center">Écart</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {extrasData?.pdj_data?.map((item, idx) => {
                                                    const limoItem = extrasData?.limo_data?.[idx] || {};
                                                    const nightItem = extrasData?.night_data?.[idx] || {};
                                                    
                                                    const renderCell = (val, isEcart = false, budget = 0) => {
                                                        if (isEcart) {
                                                            const pct = budget > 0 ? ((val / budget) * 100).toFixed(0) : 0;
                                                            return (
                                                                <td className={`px-2 py-2 text-center font-medium ${
                                                                    val >= 0 ? 'text-green-600' : 'text-red-600'
                                                                }`}>
                                                                    {val !== 0 ? (val >= 0 ? '+' : '') + pct + '%' : '-'}
                                                                </td>
                                                            );
                                                        }
                                                        return (
                                                            <td className="px-2 py-2 text-center">
                                                                {val > 0 ? formatCurrency(val) : '-'}
                                                            </td>
                                                        );
                                                    };
                                                    
                                                    return (
                                                        <tr key={idx} className="border-b border-border hover:bg-muted/20">
                                                            <td className="px-4 py-2 font-medium">{item.jour}</td>
                                                            {/* PDJ */}
                                                            {renderCell(item.budget)}
                                                            {renderCell(item.realise)}
                                                            {renderCell(item.ecart, true, item.budget)}
                                                            {/* Limonade */}
                                                            {renderCell(limoItem.budget)}
                                                            {renderCell(limoItem.realise)}
                                                            {renderCell(limoItem.ecart, true, limoItem.budget)}
                                                            {/* Night */}
                                                            {renderCell(nightItem.budget)}
                                                            {renderCell(nightItem.realise)}
                                                            {renderCell(nightItem.ecart, true, nightItem.budget)}
                                                        </tr>
                                                    );
                                                })}
                                                {/* Ligne Total */}
                                                <tr className="bg-muted/40 font-semibold">
                                                    <td className="px-4 py-3">TOTAL</td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.pdj?.budget || 0)}</td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.pdj?.realise || 0)}</td>
                                                    <td className={`px-2 py-3 text-center ${
                                                        (extrasData?.totals?.pdj?.ecart || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {extrasData?.totals?.pdj?.budget > 0 
                                                            ? ((extrasData?.totals?.pdj?.ecart / extrasData?.totals?.pdj?.budget) * 100).toFixed(0) + '%'
                                                            : '-'}
                                                    </td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.limo?.budget || 0)}</td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.limo?.realise || 0)}</td>
                                                    <td className={`px-2 py-3 text-center ${
                                                        (extrasData?.totals?.limo?.ecart || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {extrasData?.totals?.limo?.budget > 0 
                                                            ? ((extrasData?.totals?.limo?.ecart / extrasData?.totals?.limo?.budget) * 100).toFixed(0) + '%'
                                                            : '-'}
                                                    </td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.night?.budget || 0)}</td>
                                                    <td className="px-2 py-3 text-center">{formatCurrency(extrasData?.totals?.night?.realise || 0)}</td>
                                                    <td className={`px-2 py-3 text-center ${
                                                        (extrasData?.totals?.night?.ecart || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {extrasData?.totals?.night?.budget > 0 
                                                            ? ((extrasData?.totals?.night?.ecart / extrasData?.totals?.night?.budget) * 100).toFixed(0) + '%'
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default VentesDetailleesPage;
